import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  InfiniteScrollContainer,
  LoaderWrapper
} from '@/components/InfiniteScroll/styled';

interface InfiniteScrollProps {
  children: React.ReactNode;
  hasMore: boolean;
  isInfiniteLoading: boolean;
  loadingComponent: React.ReactNode;
  onInfiniteLoad: () => Promise<void>;
  useWindow?: boolean;
  threshold?: number;
  displayInverse?: boolean;
  initialLoad?: boolean;
  className?: string;
}

export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  children,
  hasMore,
  isInfiniteLoading,
  loadingComponent,
  onInfiniteLoad,
  useWindow = false,
  threshold = 250,
  displayInverse = false,
  initialLoad = true,
  className = ''
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const prevScrollHeightRef = useRef<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState(initialLoad);
  const [shouldShowLoader, setShouldShowLoader] = useState(isInfiniteLoading);

  useEffect(() => {
    let loaderTimeout: NodeJS.Timeout | null = null;

    if (shouldShowLoader) {
      loaderTimeout = setTimeout(() => {
        if (shouldShowLoader) {
          setShouldShowLoader(false);
          isLoadingRef.current = false;
        }
      }, 5000);
    }

    return () => {
      if (loaderTimeout) clearTimeout(loaderTimeout);
    };
  }, [shouldShowLoader]);

  const startLoading = () => {
    isLoadingRef.current = true;
    setShouldShowLoader(true);
  };

  const stopLoading = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        isLoadingRef.current = false;
        setShouldShowLoader(false);
      }, 100);
    });
  };

  const loadMore = async (onLoaded?: () => void) => {
    try {
      startLoading();
      await onInfiniteLoad();
      onLoaded?.();
    } catch (err) {
      console.error('Error in infinite scroll load:', err);
    } finally {
      stopLoading();
    }
  };

  const getScrollTop = () =>
    useWindow ? window.scrollY : (scrollContainerRef.current?.scrollTop ?? 0);

  const getScrollHeight = () =>
    useWindow
      ? document.documentElement.scrollHeight
      : (scrollContainerRef.current?.scrollHeight ?? 0);

  const getClientHeight = () =>
    useWindow
      ? window.innerHeight
      : (scrollContainerRef.current?.clientHeight ?? 0);

  const shouldLoadMore = () => {
    const scrollTop = getScrollTop();

    if (displayInverse) {
      return scrollTop < threshold;
    }

    const scrollHeight = getScrollHeight();
    const clientHeight = getClientHeight();
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  const afterLoadAction = () => {
    if (displayInverse && scrollContainerRef.current) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      const delta = newScrollHeight - prevScrollHeightRef.current;

      if (delta > 0) {
        scrollContainerRef.current.scrollTop = delta;
      }
    }
  };

  const handleScroll = useCallback(() => {
    if (!hasMore || isLoadingRef.current || !scrollContainerRef.current) return;

    if (!shouldLoadMore()) return;

    if (displayInverse) {
      prevScrollHeightRef.current = getScrollHeight();
    }

    loadMore(() => {
      requestAnimationFrame(() => {
        afterLoadAction();
      });
    });
  }, [hasMore, threshold, displayInverse, useWindow, onInfiniteLoad]);

  useEffect(() => {
    const scrollElement = useWindow ? window : scrollContainerRef.current;

    if (!scrollElement) return;

    let timeout: NodeJS.Timeout | null = null;

    const scrollHandler = () => {
      // Debounce scroll handling
      if (timeout) clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (!isLoadingRef.current) {
          handleScroll();
        }
      }, 100);
    };

    scrollElement.addEventListener('scroll', scrollHandler, { passive: true });

    return () => {
      if (timeout) clearTimeout(timeout);

      scrollElement.removeEventListener('scroll', scrollHandler);
    };
  }, [handleScroll, useWindow]);

  useEffect(() => {
    if (isInitialLoad && hasMore) {
      isLoadingRef.current = true;
      setShouldShowLoader(true);

      onInfiniteLoad()
        .then(() => {
          setTimeout(() => {
            isLoadingRef.current = false;
            setShouldShowLoader(false);
            setIsInitialLoad(false);
          }, 100);
        })
        .catch(err => {
          console.error('Error in initial infinite scroll load:', err);
          isLoadingRef.current = false;
          setShouldShowLoader(false);
          setIsInitialLoad(false);
        });
    }
  }, [isInitialLoad, hasMore, onInfiniteLoad]);

  useEffect(() => {
    if (!isInfiniteLoading && shouldShowLoader) {
      setTimeout(() => {
        setShouldShowLoader(false);
        isLoadingRef.current = false;
      }, 100);
    }
  }, [isInfiniteLoading, shouldShowLoader]);

  return (
    <InfiniteScrollContainer
      ref={scrollContainerRef}
      className={`infinite-scroll-container ${className}`}
    >
      {displayInverse && shouldShowLoader && (
        <LoaderWrapper>{loadingComponent}</LoaderWrapper>
      )}
      {children}
      {!displayInverse && shouldShowLoader && (
        <LoaderWrapper>{loadingComponent}</LoaderWrapper>
      )}
    </InfiniteScrollContainer>
  );
};
